
import { defineComponent, SetupContext, computed, ref } from "vue";
import NativeClient from "@/client/native-client";
import { PantryVirtualData, PLATE_DISPLAY_FORMAT } from "@/types/pantry";
import DateUtil from "@/utils/date-util";
import PantryUtil from "@/utils/pantry-util";

export default defineComponent({
  name: "PantryListItem",
  components: {},
  props: {
    data: {
      type: PantryVirtualData,
      default: null
    },
    selectMode: {
      type: Boolean,
      default: false
    },
    searchMode: {
      type: Boolean,
      default: false
    },
    value: {
      type: Number,
      default: 0,
      require: true
    }
  },
  setup(props, context: SetupContext) {
    const barValue = ref(props.value);
    const isPlateEmpty = () => {
      let result = false;
      if (props.data && props.data.info && props.data.info.plate) {
        if (props.data.info.pantryPlateId == null) {
          result = true;
        }
      }
      return result;
    };
    const getImage = () => {
      return props.data.info != null && props.data.info.icon ? props.data.info.icon : "";
    };
    const onImageError = (event: Event) => {
      NativeClient.sendDebugLog("PantryVirtualListItemView onImageError");
      if (event.target) {
        (event.target as HTMLImageElement).src = require("@/assets/images/food_noimage.png");
      }
    };
    const getName = () => {
      return props.data != null && props.data.info != null
        ? props.data.info.name
        : "";
    };
    const getPurchaseDate = () => {
      let result = "--/--/--";
      if (
        props.data != null &&
        props.data.info != null &&
        props.data.info.purchaseDate != null
      ) {
        result = DateUtil.formatYYMMDD(props.data.info.purchaseDate);
      }
      return result;
    };
    const getExpirationDate = () => {
      let result = "--/--/--";
      if (
        props.data != null &&
        props.data.info != null &&
        props.data.info.expirationDate != null
      ) {
        result = DateUtil.formatYYMMDD(props.data.info.expirationDate);
      }
      return result;
    };
    const isShowRange = () => {
      return props.data != null && props.data.info != null;
    };
    const isShowRangeThumb = () => {
      // 重量検知プレートは可変できないように対応
      let result = true;
      if (props.selectMode) {
        result = false;
      } 
      if (!(props.data != null &&
        props.data.info != null &&
        props.data.info.pantryPlateId == null)) {
        result = false;
      }
      return result;
    }
    const modelValue = computed({
      get: () => barValue.value,
      set: value => {
        NativeClient.sendDebugLog("PantryListItem value=" + value);
        (barValue.value = value)
      }
    });
    const styleRange = computed(() => {
      const percent = barValue.value;
      const result =
        `linear-gradient(to right, var(--main-thinline-color) ` +
        percent +
        `%,rgba(0,0,0,0) ` +
        percent +
        `%)`;
      return {
        background: result
      };
    });
    const favoriteImage = computed(() => {
      return props.data != null &&
        props.data.info != null &&
        props.data.info.favoriteStatus
        ? require("@/assets/images/icon_pantry_favorite_on.png")
        : require("@/assets/images/icon_pantry_favorite_off.png");
    });
    const isShowImage = () => {
      return !props.selectMode;
    };
    const getRangeValue = () => {
      let result = barValue.value;
      if (props.data != null &&
        props.data.info != null &&
        props.data.info.pantryPlateId != null) {
        const value = props.data.info.plate && props.data.info.plate.item ? props.data.info.plate.item.value : 0;
        const unit = props.data.info.plate ? props.data.info.plate.unit : null;
        if (unit) {
          if (unit.displayFormat == PLATE_DISPLAY_FORMAT.VALUE) {
            result = value;
          }
        }
      }
      return result;
    };
    const getRangeUnit = () => {
      let result = "";
      if (props.data != null &&
        props.data.info != null &&
        props.data.info.pantryPlateId != null) {
        const unit = props.data.info.plate ? props.data.info.plate.unit : null;
        if (unit) {
          if (unit.displayFormat == PLATE_DISPLAY_FORMAT.VALUE) {
            result = "個";
          } else if (unit.displayFormat == PLATE_DISPLAY_FORMAT.GAUGE) {
            result = "％";
          }
        } 
      } else {
        result = "%";
      }
      return result;
    }
    const clickCheck = () => {
      context.emit("select-check", props.data, !props.data.checked);
    };
    const clickFood = () => {
      NativeClient.sendDebugLog("PantryListItem onClickFood");
      context.emit("click-food", props.data);
    };
    const checkedImage = computed(() => {
      if (props.data && props.data.checked) {
        return require("@/assets/images/icn-check-on.png");
      }
      return require("@/assets/images/icn-check-off.png");
    });
    const onTouchEndEvent = (e:TouchEvent) => {
      NativeClient.sendDebugLog("PantryListItem onTouchEndEvent value=" + barValue.value);
      context.emit("change-value", props.data, barValue.value);
    };
    const isShowFavorite = () => {
      return (props.data.info && props.data.info.ingredientId != null);
    };
    const clickFavortite = () => {
      context.emit("click-favorite", props.data);
    }
    const clickShoplist = () => {
      context.emit("click-shoplist", props.data);
    };
    const isShowPlateError = () => {
      let result = false;
      if (
        props.data != null &&
        props.data.info != null
      ) {
        result = PantryUtil.isShowPlateError(props.data.info);
      }
      return result;
    };
    const isSelectMode = () => {
      let isSelect = false;
      const isPlate = (props.data && props.data.info && props.data.info.plate);
      // 選択中
      if (props.selectMode) {
        isSelect = true;
        if (!props.searchMode && isPlate) {
          // 検索非表示でプレート食材であれば、非表示
          isSelect = false;
        }
      }
      return isSelect;
    };
    return {
      getImage,
      getName,
      onImageError,
      getPurchaseDate,
      getExpirationDate,
      styleRange,
      modelValue,
      favoriteImage,
      isShowImage,
      isShowRange,
      getRangeValue,
      getRangeUnit,
      clickCheck,
      checkedImage,
      onTouchEndEvent,
      isShowRangeThumb,
      clickFood,
      isShowFavorite,
      clickShoplist,
      clickFavortite,
      isPlateEmpty,
      isShowPlateError,
      isSelectMode
    };
  }
});
